import React from "react";
import { CardContent } from "@/components/ui/card";
import { useDashboardData } from "@/services/dashboardDataService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Clock, AlertTriangle, FileWarning } from "lucide-react";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import { cleanDateAndTimeObject } from "@shared/renderDate";

interface SupportingDocsData {
  requiresAttention: number;
  overdueForReview: number;
  expiringSoon: number;
  totalDocuments: number;
  lastUpdated?: {
    _seconds: number;
    _nanoseconds: number;
  };
  documents?: Array<{
    id: string;
    name: string;
    status: string;
    nextReviewDate: Date | string | { _seconds: number; _nanoseconds: number } | null;
    documentType: string;
  }>;
}

const SupportingDocsRequiresAttentionWidget: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const companyId = activeTenancy?.id;
  
  // Use the dashboard data service to get the supporting docs data
  const { data, isLoading, error } = useDashboardData('supporting-docs-requires-attention') as unknown as {
    data: SupportingDocsData | null;
    isLoading: boolean;
    error: string | null;
  };

  // Report widget status to dashboard context
  useWidgetStatusReporter('supporting-docs-requires-attention', isLoading, error, !!data);
  
  // Extract the metrics from the data
  const requiresAttention = data?.requiresAttention || 0;
  const overdueCount = data?.overdueForReview || 0;
  const expiringSoon = data?.expiringSoon || 0;
  const lastUpdated = data?.lastUpdated ? cleanDateAndTimeObject(data.lastUpdated) : null;

  // Determine the color based on the number of documents requiring attention
  const numberColor = requiresAttention > 0 ? 'text-red-600' : 'text-primary';
  
  // Format the last updated time
  const formattedLastUpdated = lastUpdated ? lastUpdated.toLocaleTimeString() : null;

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <h3 className="text-lg font-semibold mb-2">Supporting Docs Require Attention</h3>
      <div
        className="flex flex-col items-center justify-center flex-1 w-full h-full"
        style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}
      >
        {/* Main count */}
        <div className="flex flex-col items-center">
          <span
            className={`font-bold ${numberColor}`}
            style={{
              fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
              lineHeight: 1,
              minHeight: '1em',
            }}
          >
            {isLoading ? (
              <Skeleton className="h-12 w-12 rounded-full" />
            ) : (
              requiresAttention
            )}
          </span>
          <span
            className="text-muted-foreground text-center"
            style={{
              fontSize: 'clamp(1rem, 2.5cqw, 1.5rem)',
              marginTop: 'clamp(0.25rem, 1cqw, 0.5rem)',
            }}
          >
            {requiresAttention === 1 ? 'document requires' : 'documents require'} attention
          </span>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">{overdueCount} overdue</span>
          </div>
          <div className="flex items-center gap-2 text-blue-600">
            <Clock className="h-4 w-4" />
            <span className="text-sm">{expiringSoon} expiring soon</span>
          </div>
        </div>

        {/* Last updated */}
        {formattedLastUpdated && (
          <div className="text-xs text-muted-foreground mt-2">
            Updated: {formattedLastUpdated}
          </div>
        )}
      </div>
      
      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error || 'Failed to load document data'}
          </AlertDescription>
        </Alert>
      )}
    </CardContent>
  );
};

export default SupportingDocsRequiresAttentionWidget;
